import { useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  priceListsApi, type PriceListType,
  itemPickerApi, warehousesApi, type ItemPickerRow,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";

const TYPES: PriceListType[] = ["OP", "IP", "Corporate", "Insurance", "CustomerSpecific"];

interface DraftLine {
  key: string;
  item: ItemPickerRow | null;
  rate: number;
  effectiveFrom: string;
  effectiveTo: string;
}
const blankLine = (): DraftLine => ({
  key: crypto.randomUUID(), item: null, rate: 0,
  effectiveFrom: new Date().toISOString().slice(0, 10), effectiveTo: "",
});

/**
 * UC-M2-PRC-01 — Price list CRUD. Lines panel uses the universal item picker (S2.1)
 * via a paste-a-UUID input in this thin first cut; a proper picker drops in S10 polish.
 */
export function PriceListsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [listType, setListType] = useState<PriceListType>("OP");
  const [priority, setPriority] = useState(100);
  const [customerId, setCustomerId] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([blankLine()]);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({ queryKey: ["price-lists"], queryFn: () => priceListsApi.list({ pageSize: 200 }) });

  // Pull the default warehouse for the picker. Items returned by the picker include
  // form/packaging/strength so the operator can sanity-check before adding a price.
  const warehouses = useQuery({
    queryKey: ["warehouses", "all"],
    queryFn: () => warehousesApi.list({ pageSize: 50 }),
    staleTime: 5 * 60_000,
  });
  const firstWarehouseId = warehouses.data?.items[0]?.id ?? "";
  const picker = useQuery({
    queryKey: ["item-picker", "pricing", firstWarehouseId],
    queryFn: () => itemPickerApi.picker({
      stockPolicy: "Any", warehouseId: firstWarehouseId, pageSize: 100,
    }),
    enabled: !!firstWarehouseId,
  });

  const save = useMutation({
    meta: { successMessage: "Price list saved" },
    mutationFn: () => priceListsApi.create({
      code, name, listType, priority,
      customerId: listType === "CustomerSpecific" ? customerId : undefined,
      lines: lines.map(l => ({
        itemId: l.item!.id, rate: l.rate,
        effectiveFrom: l.effectiveFrom,
        effectiveTo: l.effectiveTo || undefined,
      })),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["price-lists"] });
      setOpen(false); setCode(""); setName(""); setListType("OP"); setPriority(100);
      setCustomerId(""); setLines([blankLine()]);
    },
    onError: (e) => setError(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: (id: string) => priceListsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["price-lists"] }),
    onError: (e) => setError(errorMessage(e)),
  });

  const validationError =
    !code ? "Code is required." :
    !name ? "Name is required." :
    (listType === "CustomerSpecific" && !customerId) ? "Pick a customer for a customer-specific list." :
    !lines.every(l => l.item && l.rate >= 0 && l.effectiveFrom) ? "Every line needs an item, a rate (≥ 0) and an effective-from date." :
    null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Price lists</Typography>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setError(null); setOpen(true); }}>
          New price list
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Code</TableCell><TableCell>Name</TableCell>
            <TableCell>Type</TableCell><TableCell align="right">Priority</TableCell>
            <TableCell align="right">Lines</TableCell><TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {query.data?.items.map(p => (
              <TableRow key={p.id} hover>
                <TableCell><b>{p.code}</b></TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell><Chip size="small" variant="outlined" label={p.listType} /></TableCell>
                <TableCell align="right">{p.priority}</TableCell>
                <TableCell align="right">{p.lineCount}</TableCell>
                <TableCell>
                  <Chip size="small" color={p.isActive ? "success" : "default"}
                    label={p.isActive ? "Active" : "Inactive"} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="error"
                    onClick={() => { if (confirm(`Delete price list ${p.code}?`)) del.mutate(p.id); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New price list</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField label="Code *" value={code} onChange={(e) => setCode(e.target.value)} sx={{ flex: 1 }} />
              <TextField label="Name *" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex: 2 }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField select label="Type *" value={listType}
                onChange={(e) => setListType(e.target.value as PriceListType)} sx={{ minWidth: 180 }}>
                {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField type="number" label="Priority"
                value={priority} onChange={(e) => setPriority(Number(e.target.value) || 0)}
                sx={{ width: 120 }} />
              {listType === "CustomerSpecific" && (
                <TextField label="Customer id *" placeholder="UUID"
                  value={customerId} onChange={(e) => setCustomerId(e.target.value)} sx={{ flex: 1 }} />
              )}
            </Stack>

            <Typography variant="subtitle2">Lines</Typography>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={{ minWidth: 280 }}>Item *</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>Rate (₹) *</TableCell>
                <TableCell sx={{ width: 160 }}>Effective from *</TableCell>
                <TableCell sx={{ width: 160 }}>Effective to</TableCell>
                <TableCell sx={{ width: 60 }} />
              </TableRow></TableHead>
              <TableBody>
                {lines.map((l) => (
                  <TableRow key={l.key}>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        options={picker.data ?? []}
                        getOptionLabel={(o) => `${o.sku} — ${o.name}`}
                        value={l.item}
                        onChange={(_, v) =>
                          setLines(ls => ls.map(x => x.key === l.key ? { ...x, item: v } : x))}
                        renderInput={(p) => <TextField {...p} placeholder="Search item…" />}
                        loading={picker.isLoading}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" sx={{ width: 110 }}
                        value={l.rate} onChange={(e) =>
                          setLines(ls => ls.map(x => x.key === l.key ? { ...x, rate: Number(e.target.value) || 0 } : x))}
                        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                    </TableCell>
                    <TableCell>
                      <TextField type="date" size="small" InputLabelProps={{ shrink: true }}
                        value={l.effectiveFrom} onChange={(e) =>
                          setLines(ls => ls.map(x => x.key === l.key ? { ...x, effectiveFrom: e.target.value } : x))} />
                    </TableCell>
                    <TableCell>
                      <TextField type="date" size="small" InputLabelProps={{ shrink: true }}
                        value={l.effectiveTo} onChange={(e) =>
                          setLines(ls => ls.map(x => x.key === l.key ? { ...x, effectiveTo: e.target.value } : x))} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small"
                        onClick={() => setLines(ls => ls.length === 1 ? ls : ls.filter(x => x.key !== l.key))}
                        disabled={lines.length === 1}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button startIcon={<AddIcon />} size="small" sx={{ alignSelf: "flex-start" }}
              onClick={() => setLines(ls => [...ls, blankLine()])}>Add line</Button>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
          {validationError && <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!!validationError || save.isPending}
            onClick={() => save.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
